import webhookApi from './api/webhookApi';

/**
 * Service for managing webhooks
 */
const webhookService = {
  /**
   * Get all webhooks for current user
   * @param {string} userId - User ID
   * @returns {Promise<{data: Array, error: string|null}>} Webhooks data and error if any
   */
  async getWebhooks(userId) {
    try {
      const { data, error } = await webhookApi.getWebhooks(userId);
      return { data, error };
    } catch (error) {
      console.error('Error in getWebhooks:', error);
      return { data: [], error: error.message || 'Failed to fetch webhooks' };
    }
  },


  async getWebhooklists() {
    try {
      const { data, error } = await webhookApi.getWebhookLists();
      return { data, error };
    } catch (error) {
      console.error('Error in getWebhooks:', error);
      return { data: [], error: error.message || 'Failed to fetch webhooks' };
    }
  },

  /**
   * Get single webhook by ID
   * @param {string} webhookId - Webhook ID
   * @returns {Promise<{data: Object|null, error: string|null}>} Webhook data and error if any
   */
  async getWebhook(webhookId) {
    try {
      const { data, error } = await webhookApi.getWebhook(webhookId);
      if (error) {
        throw new Error(error);
      }
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching webhook:', error);
      return { data: null, error: error.message || 'Failed to fetch webhook' };
    }
  },

  /**
   * Create a new webhook
   * @param {string} userId - User ID
   * @param {Object} webhookData - Webhook data
   * @returns {Promise<{data: Object|null, error: string|null}>} Created webhook data and error if any
   */
  async createWebhook(userId, webhookData) {
    try {
      const { data, error } = await webhookApi.createWebhook({
        userId,
        name: webhookData.name,
        url: webhookData.url,
        events: webhookData.events,
        status: webhookData.status || 'active',
        secret: webhookData.secret,
        project_id: webhookData.project_id,
        headers: webhookData.headers,
        retryPolicy: webhookData.retryPolicy
      });
      
      if (error) {
        throw new Error(error);
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Error creating webhook:', error);
      return { data: null, error: error.message || 'Failed to create webhook' };
    }
  },

  /**
   * Update an existing webhook
   * @param {string} webhookId - Webhook ID
   * @param {string} userId - User ID
   * @param {Object} webhookData - Updated webhook data
   * @returns {Promise<{data: Object|null, error: string|null}>} Updated webhook data and error if any
   */
  async updateWebhook(webhookId, userId, webhookData) {
    try {
      const { data, error } = await webhookApi.updateWebhook(webhookId, {
        userId,
        name: webhookData.name,
        url: webhookData.url,
        events: webhookData.events,
        status: webhookData.status,
        secret: webhookData.secret,
        project_id: webhookData.project_id,
        headers: webhookData.headers,
        retryPolicy: webhookData.retryPolicy
      });
      return { data, error };
    } catch (error) {
      console.error('Error updating webhook:', error);
      return { data: null, error };
    }
  },

  /**
   * Delete a webhook
   * @param {string} webhookId - Webhook ID
   * @param {string} userId - User ID (for authorization)
   * @returns {Promise<{error: string|null}>} Error if any
   */
  async deleteWebhook(webhookId, userId) {
    try {
      const { error } = await webhookApi.deleteWebhook(webhookId);
      if (error) {
        throw new Error(error);
      }
      return { error: null };
    } catch (error) {
      console.error('Error deleting webhook:', error);
      return { error: error.message || 'Failed to delete webhook' };
    }
  },

  /**
   * Toggle webhook status between active and inactive
   * @param {string} webhookId - Webhook ID
   * @param {string} userId - User ID (for authorization)
   * @returns {Promise<{data: Object|null, error: string|null}>} Updated webhook data and error if any
   */
  async toggleWebhookStatus(webhookId, userId) {
    try {
      const { data: webhook, error: fetchError } = await this.getWebhook(webhookId);
      if (fetchError || !webhook) {
        throw new Error(fetchError || 'Webhook not found');
      }


      const { data, error } = await webhookApi.toggleWebhookStatus(webhookId, webhook.status);
      if (error) {
        throw new Error(error);
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Error toggling webhook status:', error);
      return { data: null, error: error.message || 'Failed to toggle webhook status' };
    }
  },

  /**
   * Test a webhook with a sample payload
   * @param {string} webhookId - Webhook ID
   * @param {string} userId - User ID (for authorization)
   * @param {Object} payload - Test payload to send
   * @returns {Promise<{data: Object|null, error: string|null}>} Test result and error if any
   */
  async testWebhook(webhookId, userId, payload) {
    try {
      const { data, error } = await webhookApi.testWebhook(webhookId, payload);
      if (error) {
        throw new Error(error);
      }
      return { data, error: null };
    } catch (error) {
      console.error('Error testing webhook:', error);
      return { data: null, error: error.message || 'Failed to test webhook' };
    }
  },

  /**
   * Get logs for a webhook
   * @param {string} webhookId - Webhook ID
   * @param {Object} filters - Filter parameters
   * @returns {Promise<{data: Array, error: string|null}>} Logs data and error if any
   */
  async getWebhookLogs(webhookId, filters = {}) {
    try {
      const { data, error } = await webhookApi.getWebhookLogs(webhookId, filters);
      if (error) {
        throw new Error(error);
      }
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching webhook logs:', error);
      return { data: [], error: error.message || 'Failed to fetch webhook logs' };
    }
  },

  /**
   * Retry a failed webhook delivery
   * @param {string} logId - Log entry ID
   * @returns {Promise<{data: Object|null, error: string|null}>} Retry result and error if any
   */
  async retryWebhookDelivery(logId) {
    try {
      const { data, error } = await webhookApi.retryWebhookDelivery(logId);
      if (error) {
        throw new Error(error);
      }
      return { data, error: null };
    } catch (error) {
      console.error('Error retrying webhook delivery:', error);
      return { data: null, error: error.message || 'Failed to retry webhook delivery' };
    }
  },

  /**
   * Get webhook statistics for a user
   * @param {string} userId - User ID
   * @returns {Promise<{data: Object|null, error: string|null}>} Statistics data and error if any
   */
  async getWebhookStats(userId) {
    try {
      const { data, error } = await webhookApi.getWebhookStats(userId);
      if (error) {
        throw new Error(error);
      }
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching webhook stats:', error);
      return { data: null, error: error.message || 'Failed to fetch webhook statistics' };
    }
  },

  /**
   * Generate a signature for webhook payload verification
   * @param {Object} payload - The payload to sign
   * @param {string} secret - Secret key for signing
   * @returns {string} Generated signature
   */
  generateSignature(payload, secret) {
    try {
      const hmac = crypto.createHmac('sha256', secret);
      hmac.update(JSON.stringify(payload));
      return `sha256=${hmac.digest('hex')}`;
    } catch (error) {
      console.error('Error generating signature:', error);
      throw new Error('Failed to generate signature');
    }
  },

  /**
   * Verify a webhook signature
   * @param {string} signature - The signature to verify
   * @param {Object} payload - The payload that was signed
   * @param {string} secret - Secret key used for signing
   * @returns {boolean} Whether the signature is valid
   */
  verifySignature(signature, payload, secret) {
    try {
      const expectedSignature = this.generateSignature(payload, secret);
      return crypto.timingSafeEqual(
        Buffer.from(signature),
        Buffer.from(expectedSignature)
      );
    } catch (error) {
      console.error('Error verifying signature:', error);
      return false;
    }
  }
};

export default webhookService;